import React from "react";
import * as Sentry from "@sentry/react";
import { usePostHog } from "posthog-js/react";
import useHilosStore from "src/hooks/useHilosStore";
import usePostHogHelpers from "src/hooks/usePostHogHelpers";
import ErrorComponent from "./ErrorComponent";

interface ErrorBoundaryProps {
  type?: "component" | "page" | "section";
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  key: number;
}

class ErrorBoundaryClass extends React.Component<
  ErrorBoundaryProps & {
    posthog: any;
    maybeStartRecording: () => void;
    session: any;
  },
  ErrorBoundaryState
> {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      key: 0,
    };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ errorInfo });

    try {
      this.props.posthog?.captureException(error, {
        feature: "error_boundary",
        type: this.props.type || "component",
      });

      const userErrorId = `${this.props.posthog?.get_distinct_id()}-${
        this.props.session?.id
      }`;
      Sentry.getCurrentHub()?.setTag("posthogId", userErrorId);
      Sentry.captureException(error);
    } catch (err) {
      console.error("Error while logging error:", err);
    }
  }

  handleReset = () => {
    try {
      this.props.maybeStartRecording();
      this.props.posthog?.capture("error_boundary_reset", {
        feature: "error_boundary",
        type: this.props.type || "component",
        error: this.state.error,
      });

      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        key: this.state.key + 1,
      });
    } catch (error) {
      console.error(error);
      window.location.reload();
    }
  };

  render() {
    const { type = "component", children } = this.props;

    if (this.state.hasError && this.state.error) {
      return (
        <ErrorComponent
          error={this.state.error}
          type={type}
          onReset={this.handleReset}
        />
      );
    }

    return <React.Fragment key={this.state.key}>{children}</React.Fragment>;
  }
}

const ErrorBoundary = (props: ErrorBoundaryProps) => {
  const posthog = usePostHog();
  const { maybeStartRecording } = usePostHogHelpers();
  const { session } = useHilosStore();

  return (
    <ErrorBoundaryClass
      {...props}
      posthog={posthog}
      maybeStartRecording={maybeStartRecording}
      session={session}
    />
  );
};

export default ErrorBoundary;
